/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { ActionType } from 'cio/models/action';
import CampaignModel from 'cio/models/campaign';
import { actionTypeName, deliveryStatesAndActionsPerType } from 'cio/utils/action-info';
import { Channel, Entities, Measure } from 'cio/utils/metrics/common';
import { Metric, summarizeMetrics } from 'cio/utils/metrics/models/metric';
import { ActionMetricRow } from 'cio/components/campaign-metrics-page/message-metrics-card';
import { inject as service } from '@ember/service';
import EnvironmentService from 'cio/services/environment';
import { findMeasureDefinition } from 'cio/utils/metrics/measure';

interface CampaignMetricsPageMessageMetricsCardCellArgs {
  campaign?: CampaignModel;
  channels: Channel[];
  row: ActionMetricRow;
  measure: Measure;
}

export default class CampaignMetricsPageMessageMetricsCardCell extends Component<CampaignMetricsPageMessageMetricsCardCellArgs> {
  @service environment!: EnvironmentService;

  get showLink() {
    if (this.args.row.isSplit) {
      return false;
    }

    if (this.args.measure === 'topic_unsubscribed') {
      return false;
    }

    if (this.args.measure === 'converted' && !this.showConverted) {
      return false;
    }

    return this.messageCanTrackMeasure;
  }

  get messageCanTrackMeasure() {
    if (this.args.row.isTotal) {
      return true;
    }

    let rowActionType = this.args.row.action.type;

    if (rowActionType === 'split_randomized_action') {
      rowActionType = 'email_action';
    } else if (rowActionType === 'multi_lang_branch_action' && 'variations' in this.args.row) {
      rowActionType = this.args.row.variations[0].action.type;
    }

    return (deliveryStatesAndActionsPerType(this.args.row.action.canConvert)?.[rowActionType] ?? []).includes(
      this.args.measure,
    );
  }

  get optionalConversions() {
    return (this.args.campaign?.messagingActions ?? []).reduce<Partial<Record<ActionType, boolean>>>((acc, action) => {
      if (acc[action.type] === true) {
        return acc;
      }

      acc[action.type] = action.canConvert;

      return acc;
    }, {});
  }

  get showConverted() {
    if (this.args.row.action?.isSplitTest) {
      return true;
    }

    return this.args.row.isTotal || this.args.row.action?.canConvert;
  }

  get totalRowData() {
    if (!this.args.row.isTotal || this.args.channels.length <= 1 || this.args.campaign?.id == null) {
      return null;
    }

    let perChannelMetrics: Partial<Record<ActionType, Metric[]>> = (this.args.row.metric.metrics ?? []).reduce<
      Partial<Record<ActionType, Metric[]>>
    >((acc, m) => {
      if (m.actionType == null) {
        return acc;
      }

      let type = m.actionType as ActionType;

      if (type === 'split_randomized_action') {
        type = 'email_action';
      }

      if (acc[type] == null) {
        acc[type] = [];
      }

      acc[type]?.push(m);

      return acc;
    }, {});

    let summarizedMetrics = Object.keys(perChannelMetrics).reduce<Partial<Record<Channel, Metric>>>((acc, type) => {
      if (this.args.campaign?.id == null) {
        return acc;
      }

      let channel = actionTypeName(type as ActionType) as Channel;
      acc[channel] = summarizeMetrics(
        this.args.campaign.id,
        Entities.CAMPAIGN,
        type as ActionType,
        perChannelMetrics[type as ActionType] ?? [],
      );
      return acc;
    }, {});

    return summarizedMetrics;
  }

  get linkRoute() {
    if (this.args.campaign == null) {
      return null;
    }

    if (this.args.campaign.isApiTriggered) {
      return 'outbox';
    }

    return 'campaigns.campaign.overview.sent';
  }

  get linkId() {
    if (this.args.campaign == null) {
      return null;
    }

    // When linking to a different route (like for API triggered broadcasts),
    // we need to specify the environment ID in order for link-to to build the
    // right URL.
    if (this.args.campaign.isApiTriggered) {
      return this.environment.current.id;
    }

    return this.args.campaign.id;
  }

  get linkQuery() {
    let query: Record<string, string | number> = {};

    let measureDefinition = findMeasureDefinition(this.args.measure);

    if (measureDefinition != null && !measureDefinition.isComputed) {
      query.deliveryStatus = measureDefinition.valueKey;
    } else {
      query.deliveryStatus = this.args.measure;
    }

    if (!this.args.row.isTotal) {
      if (!this.args.row.isSplit) {
        query.action = this.args.row.action.id;
      }

      if (this.args.row.isMultiLanguage) {
        let childActions = this.args.campaign?.getConsolidatedBranchChildActions(this.args.row.action.id);
        let defaultAction = childActions?.[childActions.length - 1];

        if (defaultAction != null) {
          query.actionType = actionTypeName(defaultAction.type);
        }
      } else {
        query.actionType = this.args.row.isSplit ? 'email' : actionTypeName(this.args.row.action.type);
      }
    }

    if (this.args.campaign?.isApiTriggered) {
      query.campaign = this.args.campaign?.id;
    }

    return query;
  }
}
